import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { takeWhile } from 'rxjs/operators';

import { AuthService } from '../../services/auth.service';
import { SpinnerService } from '../../spinner.service';

const OTP_EXPIRY_SECONDS = 120;  // 2 minutes
const MAX_ATTEMPTS = 3;

@Component({
    selector: 'app-otp-verification',
    templateUrl: './otp-verification.component.html',
    styleUrls: ['./otp-verification.component.css']
})
export class OtpVerificationComponent implements OnInit, OnDestroy {

    form: FormGroup;

    dsEmail: string = '';
    attemptsRemaining: number = MAX_ATTEMPTS;
    timeRemaining: number = OTP_EXPIRY_SECONDS;
    isLoading: boolean = false;
    errorMessage: string = '';
    canResend: boolean = false;
    origem: string = 'header';
    etapaOrigem: number = 0;

    private timerSubscription: Subscription;

    constructor(
        private authService: AuthService,
        private router: Router,
        private route: ActivatedRoute,
        private spinnerService: SpinnerService
    ) {
        this.form = new FormGroup({
            codigoOtp: new FormControl('', [
                Validators.required,
                Validators.pattern(/^\d{6}$/)
            ])
        });
    }

    ngOnInit() {
        this.route.queryParams.subscribe(params => {
            this.dsEmail = params['email'] || '';
            this.origem = params['origem'] || 'header';
            this.etapaOrigem = +(params['etapa'] || 0);
            if (!this.dsEmail) {
                this.router.navigate(['/home']);
                return;
            }
            this.startTimer();
        });
    }

    ngOnDestroy() {
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
        }
    }

    get formattedTime(): string {
        const minutes = Math.floor(this.timeRemaining / 60);
        const seconds = this.timeRemaining % 60;
        return `${this.pad(minutes)}:${this.pad(seconds)}`;
    }

    get maxAttempts(): number {
        return MAX_ATTEMPTS;
    }

    fnValidarOtp() {
        if (!this.form.valid) {
            for (const ctrl in this.form.controls) {
                this.form.controls[ctrl].markAsTouched();
            }
            return;
        }

        if (this.attemptsRemaining <= 0) {
            this.router.navigate(['/home']);
            return;
        }

        this.isLoading = true;
        this.errorMessage = '';
        this.spinnerService.start();

        const codigoOtp: string = this.form.get('codigoOtp').value;

        this.authService.validarOtp(this.dsEmail, codigoOtp).subscribe(
            result => {
                this.isLoading = false;
                this.spinnerService.stop();

                if (result.autenticado === true) {
                    this.authService.saveSession(result);
                    if (this.origem === 'planos') {
                        sessionStorage.setItem('planos_otp_success', '1');
                        this.router.navigate(['/sobre/planos']);
                    } else {
                        this.router.navigate(['/area-assinante']);
                    }
                } else {
                    this.attemptsRemaining--;
                    this.form.get('codigoOtp').reset();

                    if (this.attemptsRemaining <= 0) {
                        this.errorMessage = 'Número máximo de tentativas atingido. Redirecionando...';
                        setTimeout(() => this.router.navigate(['/home']), 2000);
                    } else {
                        this.errorMessage = `Código inválido. Verifique e tente novamente.`;
                    }
                }
            },
            err => {
                this.isLoading = false;
                this.spinnerService.stop();
                this.attemptsRemaining--;
                this.form.get('codigoOtp').reset();

                if (this.attemptsRemaining <= 0) {
                    this.errorMessage = 'Número máximo de tentativas atingido. Redirecionando...';
                    setTimeout(() => this.router.navigate(['/home']), 2000);
                } else {
                    this.errorMessage = err.error && err.error.mensagem
                        ? err.error.mensagem
                        : 'Código inválido. Verifique e tente novamente.';
                }
            }
        );
    }

    fnReenviarCodigo() {
        if (!this.canResend) { return; }
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
        }
        this.errorMessage = '';
        this.form.reset();
        this.isLoading = true;
        this.spinnerService.start();

        this.authService.reenviarOtp(this.dsEmail).subscribe(
            result => {
                this.isLoading = false;
                this.spinnerService.stop();
                if (result.otpRequired === true) {
                    this.timeRemaining = OTP_EXPIRY_SECONDS;
                    this.attemptsRemaining = MAX_ATTEMPTS;
                    this.canResend = false;
                    this.startTimer();
                } else {
                    this.errorMessage = result.mensagem || 'Não foi possível reenviar o código. Tente novamente.';
                }
            },
            err => {
                this.isLoading = false;
                this.spinnerService.stop();
                this.errorMessage = err.error && err.error.mensagem
                    ? err.error.mensagem
                    : 'Erro ao reenviar o código. Tente novamente.';
            }
        );
    }

    fnVoltarLogin() {
        this.router.navigate(['/home']);
    }

    private startTimer() {
        this.canResend = false;
        this.timerSubscription = interval(1000)
            .pipe(takeWhile(() => this.timeRemaining > 0))
            .subscribe(() => {
                this.timeRemaining--;
                if (this.timeRemaining <= 0) {
                    this.canResend = true;
                    this.errorMessage = 'O código expirou. Solicite um novo código abaixo.';
                }
            });
    }

    private pad(value: number): string {
        return value < 10 ? `0${value}` : `${value}`;
    }
}
